export const DISSOLVE_MS = 480;
export const COLLAPSE_MS = 260;

type DissolveOptions = {
	onDone?: () => void;
};

type Particle = {
	x: number;
	y: number;
	w: number;
	h: number;
	vx: number;
	vy: number;
	color: string;
};

export function prefersReducedMotion(): boolean {
	return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function resetDissolve(element: HTMLElement | null | undefined): void {
	if (!element) return;
	element.classList.remove("is-dissolving", "is-collapsing");
	element.style.height = "";
	element.style.marginTop = "";
	element.style.marginBottom = "";
	element.style.paddingTop = "";
	element.style.paddingBottom = "";
	element.style.opacity = "";
	element.style.overflow = "";
	element.removeAttribute("aria-hidden");
}

export function dissolveElement(element: HTMLElement | null | undefined, options: DissolveOptions = {}): void {
	const finish = () => options.onDone?.();
	if (!element) {
		finish();
		return;
	}

	const rect = element.getBoundingClientRect();
	if (prefersReducedMotion() || rect.width < 2 || rect.height < 2) {
		collapseElement(element, 0, finish);
		return;
	}

	const layer = spawnParticles(element, rect);
	element.classList.add("is-dissolving");
	element.style.height = `${Math.round(rect.height)}px`;
	element.style.overflow = "hidden";
	element.setAttribute("aria-hidden", "true");

	window.setTimeout(() => {
		layer.remove();
		collapseElement(element, COLLAPSE_MS, finish);
	}, DISSOLVE_MS);
}

function collapseElement(element: HTMLElement, duration: number, done: () => void): void {
	element.classList.add("is-dissolving", "is-collapsing");
	element.style.overflow = "hidden";
	const from = element.getBoundingClientRect().height || element.offsetHeight;
	element.style.height = `${Math.round(from)}px`;
	element.getBoundingClientRect();
	element.style.height = "0px";
	element.style.marginTop = "0px";
	element.style.marginBottom = "0px";
	element.style.paddingTop = "0px";
	element.style.paddingBottom = "0px";
	element.style.opacity = "0";
	if (duration <= 0) {
		done();
		return;
	}
	window.setTimeout(done, duration);
}

function spawnParticles(element: HTMLElement, rect: DOMRect): HTMLCanvasElement {
	const pad = 96;
	const dpr = Math.min(typeof devicePixelRatio === "number" ? devicePixelRatio : 1, 2);
	const canvas = document.createElement("canvas");
	canvas.className = "dissolve-layer";
	canvas.width = Math.max(1, Math.floor((rect.width + pad * 2) * dpr));
	canvas.height = Math.max(1, Math.floor((rect.height + pad * 2) * dpr));
	canvas.style.left = `${Math.round(rect.left - pad)}px`;
	canvas.style.top = `${Math.round(rect.top - pad)}px`;
	canvas.style.width = `${Math.round(rect.width + pad * 2)}px`;
	canvas.style.height = `${Math.round(rect.height + pad * 2)}px`;
	canvas.setAttribute("aria-hidden", "true");
	const ctx = canvas.getContext("2d");
	document.body.append(canvas);
	if (!ctx) return canvas;

	ctx.scale(dpr, dpr);
	const swatches = swatchesOf(element);
	const particles = particlesFor(rect, pad, swatches);
	const started = performance.now();

	const frame = (now: number) => {
		const t = Math.min(1, (now - started) / DISSOLVE_MS);
		ctx.clearRect(0, 0, rect.width + pad * 2, rect.height + pad * 2);
		ctx.globalAlpha = (1 - t) * (1 - t * 0.35);
		for (const particle of particles) {
			particle.x += particle.vx;
			particle.y += particle.vy;
			particle.vy += 0.055;
			particle.vx *= 0.992;
			ctx.fillStyle = particle.color;
			ctx.fillRect(particle.x, particle.y, particle.w, particle.h);
		}
		if (t < 1 && canvas.isConnected) requestAnimationFrame(frame);
	};
	requestAnimationFrame(frame);
	return canvas;
}

function swatchesOf(element: HTMLElement): string[] {
	const bubble = element.querySelector(".bubble") ?? element;
	const styles = getComputedStyle(bubble);
	const base = [styles.backgroundColor, styles.color, styles.borderColor].filter(
		(value) => value && value !== "transparent" && !value.includes("0, 0, 0, 0"),
	);
	return base.length ? base : ["#8aa4c7", "#dce6f2"];
}

function particlesFor(rect: DOMRect, pad: number, swatches: string[]): Particle[] {
	const mobile = typeof window !== "undefined" && window.innerWidth <= 720;
	const cell = rect.width * rect.height > 18_000 ? 11 : 8;
	const cols = Math.max(5, Math.min(mobile ? 10 : 14, Math.round(rect.width / cell)));
	const rows = Math.max(3, Math.min(mobile ? 7 : 10, Math.round(rect.height / cell)));
	const max = mobile ? 48 : 80;
	const step = Math.max(1, Math.ceil((cols * rows) / max));
	const particles: Particle[] = [];
	const cellW = rect.width / cols;
	const cellH = rect.height / rows;
	let index = 0;
	for (let row = 0; row < rows; row += 1) {
		for (let col = 0; col < cols; col += 1) {
			index += 1;
			if (index % step !== 0) continue;
			const jitter = (seed: number) => (hash(row * 31 + col + seed) - 0.5) * 1.8;
			particles.push({
				x: pad + col * cellW + jitter(1),
				y: pad + row * cellH + jitter(2),
				w: Math.max(2.5, cellW * 0.55),
				h: Math.max(2.5, cellH * 0.55),
				vx: (hash(row + col * 13) - 0.42) * 2.6,
				vy: -0.6 - hash(col * 7 + row) * 2.4,
				color: swatches[(row + col) % swatches.length] ?? "#9aa8b8",
			});
		}
	}
	return particles;
}

function hash(value: number): number {
	const x = Math.sin(value * 12.9898) * 43758.5453;
	return x - Math.floor(x);
}
